import {
  type BaseCurrentCartServiceProps,
  ApplicationError,
  Cart,
  CartV1,
  ILeanGQLCartModel,
} from './types';
import {
  AddToCartActionOption,
  APP_DEFINITION_ID,
  CartEvents,
} from '@wix/wixstores-client-core';
import { MiniCartEvents, SecondaryEvents } from '../../constants';
import { createClient } from '@wix/sdk/client';
import {
  currentCart as currentCartV1,
  currentCartV2 as currentCart,
} from '@wix/ecom';
import { getLeanGQLCartFromCart } from './getLeanGQLCartFromCart';
import { deserialize, serialize } from '../../utils/serialize';
import { getSDKAuthAndHost } from '../../utils/getSDKAuthAndHost';
import { ViewerStorage } from '../viewer-storage/ViewerStorage';
import { getCurrencyHeader } from '../../utils/headers';
import { BaseCurrentCartService } from './BaseCurrentCartService';

const CACHE_KEY = 'CURRENT_CART';

export class CurrentCartService extends BaseCurrentCartService {
  private readonly currentCartApi;
  private readonly currentCartApiV1;
  private cartPromise?: Promise<Cart>;
  private shouldRefetchOnNextGet: boolean = false;
  private readonly viewerStorage!: ViewerStorage<{
    cart: Cart;
    currency: string;
    multiLang: string;
  }>;
  private stopListeningToInstanceChanged = false;

  constructor(props: BaseCurrentCartServiceProps) {
    super(props);

    this.viewerStorage = new ViewerStorage({
      cacheKey: CACHE_KEY,
      storage: this.apis.storage,
      siteStore: this.siteStore,
    });

    const wixClient = createClient({
      ...getSDKAuthAndHost(this.siteStore),
      modules: {
        currentCart: {
          getCurrentCart: currentCart.getCurrentCart,
          removeLineItemsFromCurrentCart:
            currentCart.removeLineItemsFromCurrentCart,
          addCouponToCurrentCart: currentCart.addCouponToCurrentCart,
          removeCouponFromCurrentCart: currentCart.removeCouponFromCurrentCart,
          addLineItemsToCurrentCart: currentCart.addLineItemsToCurrentCart,
          updateLineItemsInCurrentCart:
            currentCart.updateLineItemsInCurrentCart,
          calculateCurrentCart: currentCart.calculateCurrentCart,
          estimateCurrentCart: currentCart.estimateCurrentCart,
          setDeliveryMethodForCurrentCart:
            currentCart.setDeliveryMethodForCurrentCart,
          removeDeliveryMethodFromCurrentCart:
            currentCart.removeDeliveryMethodFromCurrentCart,
          updateCurrentCart: currentCart.updateCurrentCart,
          removeGiftCardFromCurrentCart:
            currentCart.removeGiftCardFromCurrentCart,
          addGiftCardToCurrentCart: currentCart.addGiftCardToCurrentCart,
          refreshCurrentCart: currentCart.refreshCurrentCart,
          updateFormSubmissionsInCurrentCart:
            currentCart.updateFormSubmissionsInCurrentCart,
          getUpsellOptionsForCurrentCart:
            currentCart.getUpsellOptionsForCurrentCart,
        },
        currentCartV1: {
          getCurrentCart: currentCartV1.getCurrentCart,
          estimateCurrentCartTotals: currentCartV1.estimateCurrentCartTotals,
          addToCurrentCart: currentCartV1.addToCurrentCart,
          updateCurrentCart: currentCartV1.updateCurrentCart,
          updateCurrentCartLineItemQuantity:
            currentCartV1.updateCurrentCartLineItemQuantity,
          removeCouponFromCurrentCart:
            currentCartV1.removeCouponFromCurrentCart,
          removeLineItemsFromCurrentCart:
            currentCartV1.removeLineItemsFromCurrentCart,
          createCheckoutFromCurrentCart:
            currentCartV1.createCheckoutFromCurrentCart,
        },
      },
    });
    this.currentCartApiV1 = wixClient.currentCartV1;
    this.currentCartApi = wixClient.currentCart;
  }
  public init() {
    void this.getCurrentCart().catch(() => {
      //
    });

    this.listenToCurrentCartEvents();
    this.listenToSecondaryCurrentCartServiceRequests();
    this.listenToPageRestore();
  }

  private async getCurrentCartInternal() {
    if (!this.cartPromise || this.shouldRefetchOnNextGet) {
      this.cartPromise = this.getCurrentCartFromCacheOrServer();
      this.cartPromise.catch(() => {
        this.shouldRefetchOnNextGet = true;
      });
      if (this.shouldRefetchOnNextGet) {
        this.shouldRefetchOnNextGet = false;
        const cart = await this.cartPromise;
        await this.notifyChange({ cart });
      }
    }

    return this.cartPromise;
  }

  public async getCurrentCart(): Promise<Cart> {
    return this.getCurrentCartInternal();
  }

  public async getCurrentCartV1(): Promise<CartV1> {
    return this.getCurrentCartV1FromServer();
  }

  public async calculateCurrentCart(
    options?: currentCart.CalculateCurrentCartRequest,
  ): Promise<currentCart.CalculateCurrentCartResponse> {
    return this.currentCartApi.calculateCurrentCart(options);
  }

  public getUpsellOptionsForCurrentCart(): Promise<currentCart.GetUpsellOptionsForCurrentCartResponse> {
    return this.currentCartApi.getUpsellOptionsForCurrentCart();
  }

  public estimateCurrentCart(
    options?: currentCart.EstimateCurrentCartRequest,
  ): Promise<currentCart.EstimateCurrentCartResponse> {
    return this.currentCartApi.estimateCurrentCart(options);
  }

  public invalidateAndReload(): Promise<void> {
    this.shouldRefetchOnNextGet = true;
    this.invalidateCurrentCartCache();
    void this.getCurrentCartInternal().catch(() => {});
    return Promise.resolve();
  }

  public async updateLineItemsInCurrentCart(
    options: currentCart.UpdateLineItemsInCurrentCartOptions,
  ) {
    const { cart } = await this.currentCartApi.updateLineItemsInCurrentCart(
      options,
    );
    await this.updateCache(cart!);
    return cart!;
  }

  public async removeGiftCardFromCurrentCart({
    giftCardId,
  }: {
    giftCardId: string;
  }) {
    const { cart } = await this.currentCartApi.removeGiftCardFromCurrentCart(
      giftCardId,
    );
    await this.updateCache(cart!);
    return cart!;
  }

  public async addGiftCardToCurrentCart({
    giftCardInput,
  }: {
    giftCardInput: currentCart.GiftCardInput;
  }) {
    const { cart } = await this.currentCartApi.addGiftCardToCurrentCart(
      giftCardInput,
    );
    await this.updateCache(cart!);
    return cart!;
  }

  public async removeLineItemsFromCurrentCart({
    lineItemIds,
  }: {
    lineItemIds: string[];
  }) {
    const { cart } = await this.currentCartApi.removeLineItemsFromCurrentCart(
      lineItemIds,
    );
    await this.updateCache(cart!);
    return cart!;
  }

  public async addCouponToCurrentCart({
    coupon,
  }: {
    coupon: currentCart.CouponInput;
  }) {
    const { cart } = await this.currentCartApi.addCouponToCurrentCart(coupon);
    await this.updateCache(cart!);
    return cart!;
  }

  public async removeCouponFromCurrentCart({
    couponId,
  }: {
    couponId: string;
  }): Promise<Cart> {
    const { cart } = await this.currentCartApi.removeCouponFromCurrentCart(
      couponId,
    );
    await this.updateCache(cart!);
    return cart!;
  }

  public async setDeliveryMethodForCurrentCart({
    deliveryMethod,
  }: {
    deliveryMethod: currentCart.DeliveryMethodInput;
  }) {
    const { cart } = await this.currentCartApi.setDeliveryMethodForCurrentCart(
      deliveryMethod,
    );
    await this.updateCache(cart!);
    return cart!;
  }

  public async removeDeliveryMethodFromCurrentCart() {
    const { cart } =
      await this.currentCartApi.removeDeliveryMethodFromCurrentCart();
    await this.updateCache(cart!);
    return cart!;
  }

  public async addLineItemsToCurrentCart(
    options: currentCart.AddLineItemsToCurrentCartOptions,
  ): Promise<Cart> {
    const { cart } = await this.currentCartApi.addLineItemsToCurrentCart(
      options,
    );
    if (!cart) {
      throw new Error('Failed to Add Line Items To Current Cart');
    }
    await this.updateCache(cart);
    return cart;
  }

  public async updateCurrentCart(cart: Cart): Promise<Cart> {
    const { cart: updatedCart } = await this.currentCartApi.updateCurrentCart(
      cart,
    );
    await this.updateCache(updatedCart!);
    return updatedCart!;
  }

  public async refreshCurrentCart(): Promise<Cart> {
    const { cart } = await this.currentCartApi.refreshCurrentCart();
    await this.updateCache(cart!);
    return cart!;
  }

  private notifyChange({ cart }: { cart: Cart }) {
    this.runCallbacks({ revision: cart.revision });
    this.publishUpdateForSecondaryCurrentCart();
    return this.updateMiniCart(cart);
  }

  public async updateFormSubmissionsInCurrentCart(
    options: currentCart.UpdateFormSubmissionsInCurrentCartOptions,
  ): Promise<Cart> {
    const { cart } =
      await this.currentCartApi.updateFormSubmissionsInCurrentCart(options);
    await this.updateCache(cart!);
    return cart!;
  }

  private listenToSecondaryCurrentCartServiceRequests() {
    this.subscribeToSecondaryEvent(
      SecondaryEvents.GetRequest,
      this.getCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.GetV1Request,
      this.getCurrentCartV1.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.GetCurrentCartIdFromCacheRequest,
      this.getCurrentCartIdFromCache.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.RefreshV1Request,
      this.invalidateAndReload.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.UpdateLineItemsInCurrentCartRequest,
      this.updateLineItemsInCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.RemoveLineItemsFromCurrentCartRequest,
      this.removeLineItemsFromCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.OnLoginStartRequest,
      this.onLoginStart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.OnLoginFinishRequest,
      this.onLoginFinish.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.AddCouponToCurrentCartRequest,
      this.addCouponToCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.RemoveCouponFromCurrentCartRequest,
      this.removeCouponFromCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.SetDeliveryMethodForCurrentCartRequest,
      this.setDeliveryMethodForCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.RemoveDeliveryMethodFromCurrentCartRequest,
      this.removeDeliveryMethodFromCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.CalculateCurrentCart,
      this.calculateCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.EstimateCurrentCart,
      this.estimateCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.GetUpsellOptionsForCurrentCart,
      this.getUpsellOptionsForCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.AddLineItemsToCurrentCart,
      this.addLineItemsToCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.UpdateCurrentCartRequest,
      this.updateCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.RefreshCurrentCartRequest,
      this.refreshCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.RemoveGiftCardFromCurrentCartRequest,
      this.removeGiftCardFromCurrentCart.bind(this),
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.AddGiftCardToCurrentCartRequest,
      this.addGiftCardToCurrentCart.bind(this),
    );

    this.apis.pubSub.subscribe(
      SecondaryEvents.HEALTH_CHECK,
      () => {
        this.apis.pubSub.publish(SecondaryEvents.ACK, {}, false);
      },
      false,
    );
    this.subscribeToSecondaryEvent(
      SecondaryEvents.UpdateFormSubmissionsInCurrentCartRequest,
      this.updateFormSubmissionsInCurrentCart.bind(this),
    );
  }

  private subscribeToSecondaryEvent<T, S extends unknown[]>(
    event: string,
    performAction: (...payload: S) => Promise<T>,
  ) {
    this.apis.pubSub.subscribe(
      event,
      async ({ data }) => {
        const { key, payload } = deserialize(data);
        let actionResponse;
        let error;
        try {
          actionResponse = await performAction(...((payload || []) as S));
        } catch (e) {
          actionResponse = undefined;
          error = e;
        }
        this.apis.pubSub.publish(
          SecondaryEvents.ActionResponse,
          serialize({ key, payload: actionResponse, error }),
          false,
        );
      },
      false,
    );
  }

  private listenToCurrentCartEvents() {
    let currency = this.siteStore.location.query.currency;
    this.siteStore.location.onChange(() => {
      if (currency !== this.siteStore.location.query.currency) {
        currency = this.siteStore.location.query.currency;

        void this.invalidateAndReload();
      }
    });

    this.siteStore.pubSubManager.subscribe(
      CartEvents.CLEARED,
      (res) => void this.clearCart(res.data.cartId),
    );

    this.siteStore.pubSubManager.subscribe(CartEvents.INVALIDATED, () => {
      this.invalidateCurrentCartCache();
    });

    this.siteStore.siteApis.onInstanceChanged(() => {
      if (this.stopListeningToInstanceChanged) {
        return;
      }
      void this.invalidateAndReload();
    }, APP_DEFINITION_ID);

    this.siteStore.pubSubManager.subscribe(
      MiniCartEvents.LoadedWithoutData,
      () => {
        this.getMiniCartLeanModel().then((cart) => {
          this.siteStore.pubSubManager.publish(
            MiniCartEvents.OnInitialData,
            cart,
          );
        });
      },
    );
  }

  public onLoginStart() {
    this.stopListeningToInstanceChanged = true;
    return Promise.resolve();
  }

  public onLoginFinish() {
    this.stopListeningToInstanceChanged = false;
    return Promise.resolve();
  }

  private async getMiniCartLeanModel(cart?: Cart): Promise<ILeanGQLCartModel> {
    return getLeanGQLCartFromCart({
      currentCartService: this,
      wixApi: this.context.wixApi!,
      platformServices: this.context.platformServices!,
      cart,
    });
  }

  private updateCache(cart: Cart) {
    this.cartPromise = Promise.resolve(cart);
    this.shouldRefetchOnNextGet = false;
    this.viewerStorage.set(this.toCacheEntity(cart));
    return this.notifyChange({ cart });
  }

  private toCacheEntity(cart: Cart) {
    return {
      cart,
      currency: getCurrencyHeader(this.siteStore.wixCodeApi),
      multiLang: this.siteStore.getMultiLangHeader(),
    };
  }

  private invalidateCurrentCartCache() {
    this.viewerStorage.remove();
  }

  private publishUpdateForSecondaryCurrentCart() {
    this.siteStore.pubSubManager.publish(SecondaryEvents.Updated, {});
  }

  private async updateMiniCart(cart?: Cart) {
    this.siteStore.pubSubManager.publish(CartEvents.CHANGED, {
      ...(await this.getMiniCartLeanModel(cart)),
      extraParams: { origin: 'CurrentCartUpdate' },
      eventOptions: {
        shouldOpenCart: false,
        addToCartActionOption: AddToCartActionOption.MINI_CART,
        originatedInWorker: true,
      },
      reason: 'CurrentCartUpdate',
    });
  }

  private async clearCart(givenCartId: string) {
    const id = (await this.getCurrentCart())._id;
    if (givenCartId === id) {
      const cart = { lineItems: [] };
      this.cartPromise = Promise.resolve(cart);
      this.shouldRefetchOnNextGet = false;
      await this.notifyChange({ cart });
      this.invalidateCurrentCartCache();
    }
  }

  public getCurrentCartIdFromCache(): Promise<string | undefined> {
    const cached = this.viewerStorage.get();
    if (cached && this.isCachedCartValid(cached)) {
      return Promise.resolve(cached.cart._id);
    }
    return Promise.resolve(undefined);
  }

  private async getCurrentCartFromCacheOrServer() {
    if (!this.shouldRefetchOnNextGet) {
      const cached = this.viewerStorage.get();
      if (cached && this.isCachedCartValid(cached)) {
        return cached.cart;
      }
    }
    const cart = (await this.getCurrentCartFromServer())!;
    this.viewerStorage.set(this.toCacheEntity(cart));
    return cart;
  }

  private isCachedCartValid(cachedCart: {
    cart: Cart;
    currency: string;
    multiLang: string;
  }) {
    return (
      cachedCart.currency === getCurrencyHeader(this.siteStore.wixCodeApi) &&
      cachedCart.multiLang === this.siteStore.getMultiLangHeader()
    );
  }

  private async getCurrentCartFromServer() {
    try {
      const { cart } = await this.currentCartApi.getCurrentCart();
      return cart ?? { lineItems: [] };
    } catch (e: unknown) {
      if (
        (e as ApplicationError)?.details?.applicationError?.code ===
        'CURRENT_CART_NOT_FOUND'
      ) {
        return { lineItems: [] };
      }

      throw e;
    }
  }

  private async getCurrentCartV1FromServer() {
    try {
      return await this.currentCartApiV1.getCurrentCart();
    } catch (e: unknown) {
      if (
        (e as ApplicationError)?.details?.applicationError?.code ===
        'OWNED_CART_NOT_FOUND'
      ) {
        return { lineItems: [] };
      }

      throw e;
    }
  }
  private listenToPageRestore() {
    this.context.wixApi!.window.onPageRestore?.(() => {
      this.cartPromise = undefined;
      this.shouldRefetchOnNextGet = false;

      void this.getCurrentCart().then((cart) => this.notifyChange({ cart }));
    });
  }
}
